import type { ChatDisplayVariant, NormalizedTextComChatEvent } from './events';

export type LiveChatAnalyticsEvent = {
  event: string;
  event_name: string;
  // Required by @gartner-digital-markets/analytics' sendEvent signature
  // (Capterra) and GetApp's own generic dataLayer helper's eventGA4Name
  // fallback — both pre-date this schema and expect a bare `name`
  // alongside `event`. Same value as `event`/`event_name`.
  name: string;
  chat_conversation_id?: string;
  chat_provider: 'text';
  chat_fallback_reason?: NormalizedTextComChatEvent['fallbackReason'];
  chat_brand?: string;
  chat_surface?: string;
  chat_display_variant?: ChatDisplayVariant;
  conversion_method: 'chat';
};

/**
 * Shapes a normalized `chat_*` event for forwarding to each app's own
 * dataLayer/GTM helper. Every event carries the conversation id (joins a
 * chat's full lifecycle across events, and — via the same value set as a
 * text.com session variable — to LAS's server-side conversion, see
 * `textcom-webhook/route.ts`), the vendor (the only place a vendor name is
 * allowed to live), and the conversion method dimension used to segment
 * chat alongside Form/Webcall.
 *
 * Deliberately does NOT attach page-level monetization context —
 * each brand already computes its own `monetization_type`/
 * `ppl_monetization_type` shape independently (no shared util exists for
 * that today, see `Monetization.ts`/`monetization.js`/`monetization.ts`
 * per app) — the calling app's `onEvent` handler spreads its own
 * already-computed monetization fields onto this event before forwarding,
 * the same place it already enriches other GA4 events.
 */
export const createLiveChatAnalyticsEvent = (
  event: NormalizedTextComChatEvent
): LiveChatAnalyticsEvent => ({
  event: event.name,
  event_name: event.name,
  name: event.name,
  chat_conversation_id: event.conversationId,
  chat_provider: 'text',
  chat_brand: event.brand,
  chat_surface: event.surface,
  ...(event.displayVariant && {
    chat_display_variant: event.displayVariant,
  }),
  ...(event.fallbackReason && { chat_fallback_reason: event.fallbackReason }),
  conversion_method: 'chat',
});
